import {sleep} from '@benchling/js-utils';

import {DeepResearchToolType} from 'llm/deep-research/tool-details/types';
import * as Net from 'util/Net';

const POLL_TIMEOUT_MS = 1000;

export interface InitiateDeepResearchMessageResponse {
  chatId: string;
  statusUrl: string;
}

export interface SendDeepResearchMessageResponseCompleted {
  type: 'completed';
  response: string;
}

export interface SendDeepResearchMessageResponseFailed {
  type: 'failed';
  errorMessage: string;
}

export type SendDeepResearchMessageResponse =
  | SendDeepResearchMessageResponseCompleted
  | SendDeepResearchMessageResponseFailed;

// LLM Message Block Types - mirroring Python backend structure

export interface LLMTextBlock {
  type: 'text';
  value: {
    text: string;
  };
}

export type LLMImageMediaType = 'image/jpeg' | 'image/png' | 'image/webp';

export interface LLMImageBlock {
  type: 'image';
  value: {
    base64_image: string;
    media_type: LLMImageMediaType;
  };
}

export interface LLMToolCallBlock {
  type: 'tool_call';
  value: {
    id: string;
    name: DeepResearchToolType;
    args: Record<string, unknown>;
  };
}

export interface LLMToolResultBlock {
  type: 'tool_result';
  value: {
    id: string;
    content: Array<LLMTextBlock | LLMImageBlock>;
  };
}

// These types are defined in the message_block_as_dict function in llm_message_utils.py
export type LLMMessageBlock = LLMTextBlock | LLMImageBlock | LLMToolCallBlock | LLMToolResultBlock;

export enum LLM_ROLES {
  USER = 'user',
  ASSISTANT = 'assistant',
}

export interface LLMMessage {
  role: LLM_ROLES;
  messageBlocks: Array<LLMMessageBlock>;
  createdAt: string;
}

export interface LLMConversationHistory {
  conversationId: string;
  messages: Array<LLMMessage>;
}

export interface LLMChatNode {
  chatId: string;
  conversationId: string;
  parentConversationId: string | null;
  children: Array<LLMChatNode>;
}

export interface LLMChat {
  chatId: string;
  userApiIdentifier: string;
  createdAt: string;
  modifiedAt: string;
  rootNode: LLMChatNode;
  userFacingNode: LLMChatNode;
  errorMessage: string | null;
}

export interface ProgressDataResponse {
  chatMetadata: LLMChat;
  conversationHistories: Record<string, LLMConversationHistory>;
}

/**
 * Extracts the final answer from the citation agent in the root conversation.
 * Returns the answer string if found, or null if it doesn't exist.
 */
export function getCompleteAnswer(progressData: ProgressDataResponse): string | null {
  const userFacingConversationId = progressData.chatMetadata.userFacingNode.conversationId;
  const userFacingConversation = progressData.conversationHistories[userFacingConversationId];

  if (!userFacingConversation) {
    return null;
  }

  // Find the last assistant message in the user facing conversation (which is from the citation agent)
  const assistantMessage = userFacingConversation.messages
    .slice()
    .reverse()
    .find((message) => message.role === LLM_ROLES.ASSISTANT);

  if (!assistantMessage) {
    return null;
  }

  const responseBlock = assistantMessage.messageBlocks.find((block) => block.type === 'text');
  return responseBlock?.value.text || null;
}

export function getUserMessage(progressData: ProgressDataResponse): string | null {
  const userFacingConversationId = progressData.chatMetadata.userFacingNode.conversationId;
  const userFacingConversation = progressData.conversationHistories[userFacingConversationId];

  if (!userFacingConversation) {
    return null;
  }

  // Check if the user facing conversation has any assistant messages
  const userMessage = userFacingConversation.messages.find((message) => message.role === LLM_ROLES.USER);

  if (!userMessage) {
    return null;
  }

  const responseBlock = userMessage.messageBlocks.find((block) => block.type === 'text');
  return responseBlock?.value.text || null;
}

/**
 * Initiates a deep research message request and returns immediately with the chatId and statusUrl
 * This allows the chatId to be persisted before the potentially lengthy polling process begins
 */
export async function initiateDeepResearchMessage(
  chatId: string | undefined,
  message: string,
  publicDataEnabled: boolean,
  emailNotificationEnabled: boolean
): Promise<InitiateDeepResearchMessageResponse> {
  const initialResponse = await Net.postJSONPromise({
    url: '/1/api/llm/deep-research',
    data: {chatId, message, publicDataEnabled, emailNotificationEnabled},
  });
  return {
    chatId: initialResponse.chatId,
    statusUrl: initialResponse.status_url,
  };
}

async function getResult(
  chatProgress: ProgressDataResponse,
  statusUrl: string | null
): Promise<SendDeepResearchMessageResponse | null> {
  // TODO: We should not rely on this.
  if (statusUrl) {
    const taskStatus = await Net.getJSONPromise(statusUrl);
    if (taskStatus.status === 'FAILURE') {
      return {type: 'failed', errorMessage: 'An error occurred. Please try again later.'};
    }
    if (taskStatus.status === 'SUCCESS') {
      return {type: 'completed', response: ''};
    }
  }
  return null;
}

/**
 * Polls the deep research message task until completion and returns the result.
 */
export async function pollDeepResearchMessage(
  chatId: string,
  statusUrl: string | null,
  abortSignal: AbortSignal | undefined,
  onProgress: (progressData: ProgressDataResponse, result: SendDeepResearchMessageResponse | null) => boolean
): Promise<void> {
  while (true) {
    if (abortSignal?.aborted) {
      throw new DOMException('Deep research polling aborted', 'AbortError');
    }
    const chatProgress = await Net.getJSONPromise(`/1/api/llm/deep-research/chat/${chatId}`);
    const maybeResult = await getResult(chatProgress, statusUrl);
    const shouldContinuePolling = onProgress(chatProgress, maybeResult);
    if (!shouldContinuePolling) {
      return;
    }
    await sleep(POLL_TIMEOUT_MS);
  }
}
